/**
 * Turns selected Work task rows into a task-level Visit bulk selection: the Visits the tasks sit on, and per Visit the task rows to act on. Approval and movement act
 * on those rows only, never the rest of the Visit. A task nothing on this board can change is left out and
 * counted: resolved work, and work of a plan that is not Started (only a Started plan's work is approved,
 * moved or withdrawn).
 */
import { heldByPerson, ServiceHistoryCompletionStatus, ServicePlanStatusSystemName, WorkTaskScheduleRow } from '@jms/model';
import { WorkTaskScheduleVisit, WorkTaskScheduleVisitSelection } from './work.task.schedule.bulk.model';

/** Called by the Work bulk commands when the menu is built, again when one runs, and by the selection chip. */
export function selectWorkTaskScheduleVisits(rows: readonly Partial<WorkTaskScheduleRow>[]): WorkTaskScheduleVisitSelection {

  const visits = new Map<string, WorkTaskScheduleVisit>();
  const taskRows: Record<string, string[]> = {};
  let tasks = 0;
  let resolved = 0;
  let stopped = 0;
  for (const row of rows) {
    if (row.completionStatus === ServiceHistoryCompletionStatus.Resolved) {
      resolved++;
      continue;
    }
    if (row.planStatus && row.planStatus !== ServicePlanStatusSystemName.Started) {
      stopped++;
      continue;
    }
    // A row the reader could not place on a Visit or name as a task row has nothing an action could name.
    if (!row.visitLocator || !row.occurrenceId || !row.visitTaskId) continue;
    tasks++;
    (taskRows[row.visitLocator] ??= []).push(row.visitTaskId);
    const known = visits.get(row.visitLocator);
    // The Visit carries a selected task's approval when any has one, so Reschedule offers Keep approved.
    if (known && !heldByPerson(known.approvalStatus) && heldByPerson(row.approvalStatus)) known.approvalStatus = row.approvalStatus;
    if (known) continue;
    visits.set(row.visitLocator, {
      _id: row.visitLocator,
      occurrenceId: row.occurrenceId,
      customer: row.customer,
      site: row.site,
      servicePlan: row.servicePlan,
      serviceLine: row.serviceLine,
      job: row.job,
      approvalStatus: row.approvalStatus,
    });
  }
  const notes = [
    resolved ? `${counted(resolved)} already resolved` : undefined,
    stopped ? `${counted(stopped)} on a paused or archived Service Plan` : undefined,
  ].filter(Boolean);

  return {
    visits: [...visits.values()],
    tasks,
    rows: taskRows,
    ...(notes.length ? { note: `Left out: ${notes.join('; ')}.` } : {}),
  };
}

/** The bulk palette's scope chip: "12 tasks on 5 Visits", or nothing when no selected task can act. */
export function describeWorkTaskScheduleSelection(rows: readonly Partial<WorkTaskScheduleRow>[]): string | null {

  const selection = selectWorkTaskScheduleVisits(rows);
  if (!selection.visits.length) return null;

  return `${plural(selection.tasks, 'task')} on ${plural(selection.visits.length, 'Visit')}`;
}

function counted(count: number): string {

  return `${plural(count, 'selected task')}`;
}

function plural(count: number, noun: string): string {

  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}
